import * as i0 from '@angular/core';
import { forwardRef, Component, Input, NgModule, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Output, Injectable } from '@angular/core';
import * as i1$1 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@ng-select/ng-select';
import { NgSelectModule } from '@ng-select/ng-select';
import * as i3 from '@ionic/angular';
import { IonicModule } from '@ionic/angular';
import * as i4 from 'ngx-autosize';
import { AutosizeModule } from 'ngx-autosize';
import { format } from 'date-fns';
import * as i4$1 from '@busacca/ng-pick-datetime';
import { OwlDateTimeIntl, OwlDateTimeModule, OwlNativeDateTimeModule } from '@busacca/ng-pick-datetime';
import * as i4$2 from 'ngx-color-picker';
import { ColorPickerModule } from 'ngx-color-picker';
import * as i4$3 from 'ngx-quill';
import { QuillModule } from 'ngx-quill';
import { pull } from 'lodash';

var ValidationErrorLevel;
(function (ValidationErrorLevel) {
    ValidationErrorLevel["warning"] = "warning";
    ValidationErrorLevel["error"] = "error";
})(ValidationErrorLevel || (ValidationErrorLevel = {}));
var InputMode;
(function (InputMode) {
    InputMode["TEXT"] = "text";
    InputMode["DECIMAL"] = "decimal";
    InputMode["NUMERIC"] = "numeric";
    InputMode["TEL"] = "tel";
    InputMode["SEARCH"] = "search";
    InputMode["EMAIL"] = "email";
    InputMode["URL"] = "url";
})(InputMode || (InputMode = {}));

class FormLabelComponent {
    label;
    formControl;
    isRequired;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    ngOnInit() {
        this.isRequired = this.hasRequiredField(this.formControl);
    }
    writeValue(value) { }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    hasRequiredField(abstractControl) {
        if (abstractControl.validator) {
            const validator = abstractControl.validator({});
            if (validator && validator['required']) {
                return true;
            }
        }
        if (abstractControl['controls']) {
            for (const controlName in abstractControl['controls']) {
                if (abstractControl['controls'][controlName]) {
                    if (this.hasRequiredField(abstractControl['controls'][controlName])) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormLabelComponent, selector: "form-label", inputs: { label: "label", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormLabelComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<p class=\"_label\" *ngIf=\"label\">{{label}}<span \n    class=\"_required-tag\" *ngIf=\"isRequired\" [ngClass]=\"{'_not-valid': formControl.validator && formControl.validator(formControl) && formControl.validator(formControl)?.['required']}\"> *</span>\n</p>", styles: ["._label{margin:0 0 3px;color:gray;text-align:left;font-size:14px}._label span{font-weight:700}._label span._not-valid{color:#eb4b24!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-label', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormLabelComponent),
                            multi: true,
                        },
                    ], template: "<p class=\"_label\" *ngIf=\"label\">{{label}}<span \n    class=\"_required-tag\" *ngIf=\"isRequired\" [ngClass]=\"{'_not-valid': formControl.validator && formControl.validator(formControl) && formControl.validator(formControl)?.['required']}\"> *</span>\n</p>", styles: ["._label{margin:0 0 3px;color:gray;text-align:left;font-size:14px}._label span{font-weight:700}._label span._not-valid{color:#eb4b24!important}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormValidationErrorComponent {
    control;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: { control: "control" }, ngImport: i0, template: "<div class=\"_validation-errors\" *ngIf=\"control?.errors && !(control.validator && control.validator(control) && control.validator(control)?.['required'] && !control.dirty && !control.touched)\">\n    <div *ngFor=\"let item of control.errors | keyvalue\" class=\"{{'_validation-error ' + item.value.level}}\">\n        <p class=\"_validation-message\" *ngIf=\"item.value.message\">{{item.value.message}}</p>\n    </div>\n</div>\n", styles: ["._validation-errors ._validation-error{display:flex;flex-direction:row;align-items:center}._validation-errors ._validation-error ion-icon{font-size:17px;margin-right:5px;margin-top:0}._validation-errors ._validation-error p{margin-top:4px;margin-bottom:0;font-size:12px}._validation-errors .warning{color:orange}._validation-errors .error{color:red;font-weight:700}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.KeyValuePipe, name: "keyvalue" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-validation-error', template: "<div class=\"_validation-errors\" *ngIf=\"control?.errors && !(control.validator && control.validator(control) && control.validator(control)?.['required'] && !control.dirty && !control.touched)\">\n    <div *ngFor=\"let item of control.errors | keyvalue\" class=\"{{'_validation-error ' + item.value.level}}\">\n        <p class=\"_validation-message\" *ngIf=\"item.value.message\">{{item.value.message}}</p>\n    </div>\n</div>\n", styles: ["._validation-errors ._validation-error{display:flex;flex-direction:row;align-items:center}._validation-errors ._validation-error ion-icon{font-size:17px;margin-right:5px;margin-top:0}._validation-errors ._validation-error p{margin-top:4px;margin-bottom:0;font-size:12px}._validation-errors .warning{color:orange}._validation-errors .error{color:red;font-weight:700}\n"] }]
        }], propDecorators: { control: [{
                type: Input
            }] } });

class FormInputComponent {
    label;
    type = 'text';
    inputmode = InputMode.TEXT;
    readonly = false;
    autocomplete = 'off';
    formControl;
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormInputComponent, selector: "form-input", inputs: { label: "label", type: "type", inputmode: "inputmode", readonly: "readonly", autocomplete: "autocomplete", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormInputComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input [type]=\"type\" [attr.inputmode]=\"inputmode\" [name]=\"controlName\" [formControl]=\"formControl\" [autocomplete]=\"autocomplete === 'off' ? 'no-auto' : autocomplete\" [readonly]=\"readonly\">\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormInputComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input [type]=\"type\" [attr.inputmode]=\"inputmode\" [name]=\"controlName\" [formControl]=\"formControl\" [autocomplete]=\"autocomplete === 'off' ? 'no-auto' : autocomplete\" [readonly]=\"readonly\">\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], type: [{
                type: Input
            }], inputmode: [{
                type: Input
            }], readonly: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormValidationErrorModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorModule, declarations: [FormValidationErrorComponent], imports: [CommonModule], exports: [FormValidationErrorComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormValidationErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FormValidationErrorComponent],
                    exports: [FormValidationErrorComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormLabelModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormLabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormLabelModule, declarations: [FormLabelComponent], imports: [CommonModule], exports: [FormLabelComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormLabelModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormLabelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FormLabelComponent],
                    exports: [FormLabelComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormInputModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormInputModule, declarations: [FormInputComponent], imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule], exports: [FormInputComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormInputModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormInputComponent],
                    exports: [FormInputComponent],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormSelectComponent {
    label;
    placeholder;
    items;
    bindValue;
    bindLabel;
    formControl;
    multiple = true;
    position = 'auto';
    readonly = false;
    addTag = false;
    template;
    searchable = true;
    selected = new EventEmitter();
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnChanges(changes) {
        if (changes['items']?.currentValue) {
            this.items?.map((item) => {
                if (this.bindLabel) {
                    item.label = item[this.bindLabel];
                }
            });
        }
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onSelect(change) {
        this.selected.emit(change);
    }
    setFocus(event) {
        const element = event.target;
        element.closest('.input')?.querySelector('input')?.focus();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormSelectComponent, selector: "form-select", inputs: { label: "label", placeholder: "placeholder", items: "items", bindValue: "bindValue", bindLabel: "bindLabel", formControl: "formControl", multiple: "multiple", position: "position", readonly: "readonly", addTag: "addTag", template: "template", searchable: "searchable" }, outputs: { selected: "selected" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormSelectComponent),
                multi: true,
            },
        ], usesOnChanges: true, ngImport: i0, template: "\n\n<div class=\"input\">\n\n    <div class=\"_label-container\">\n        <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n        <ng-content select=\"[add-icon]\"></ng-content>\n\t</div>\n\n    <div class=\"_select-container\" (click)=\"setFocus($event)\">\n        <ng-container *ngIf=\"!template\">\n            <ng-select\n                class=\"_select\"\n                [placeholder]=\"placeholder\"\n                addTagText=\"+\"\n                [bindValue]=\"bindValue\"\n                [bindLabel]=\"bindLabel\"\n                [name]=\"controlName\" \n                [formControl]=\"formControl\"\n                [items]=\"items\"\n                [multiple]=\"multiple\"\n                [closeOnSelect]=\"!multiple\"\n                [dropdownPosition]=\"position\"\n                [readonly]=\"readonly\"\n                [addTag]=\"addTag\"\n                (change)=\"onSelect($event)\"\n                [searchable]=\"searchable\"\n            >\n            </ng-select>    \n        </ng-container>\n    \n        <ng-container *ngIf=\"template\">\n            <ng-select\n                class=\"_select\"\n                [placeholder]=\"placeholder\"\n                addTagText=\"+\"\n                [name]=\"controlName\" \n                [formControl]=\"formControl\"\n                [multiple]=\"multiple\"\n                [closeOnSelect]=\"!multiple\"\n                [dropdownPosition]=\"position\"\n                [readonly]=\"readonly\"\n                [addTag]=\"addTag\"\n                [bindLabel]=\"bindLabel\"\n                (change)=\"onSelect($event)\"\n                [searchable]=\"searchable\"\n                >\n                <ng-option *ngFor=\"let item of items\" [value]=\"bindValue === 'item' ? $any(item) : bindValue ? $any(item)[bindValue] : $any(item).id\" [bindLabel]=\"$any(item)[bindLabel]\">\n                    <ng-container *ngTemplateOutlet=\"template; context: {$implicit: item}\"></ng-container>\n                </ng-option>\n            </ng-select>    \n        </ng-container>\n\n        <ng-content select=\"[add-button]\"></ng-content>\n    </div>\n\n    \n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: [".input ._label-container{margin-bottom:3px;display:flex;flex-direction:row;align-items:center;justify-content:space-between}.input ._label-container ion-icon{margin-right:10px}.input ._label-container ion-icon:hover{cursor:pointer}.input ._select-container{display:flex;align-items:center;flex-direction:row}.input ._select-container ng-select._select{flex-grow:10}.input ._select-container ng-select.ng-select-single ::ng-deep .ng-value-container .ng-value .ng-value-icon{display:none!important}.input ._select-container ng-select.ng-select-single ::ng-deep .ng-value-container .ng-value .ng-value-label{padding:0 5px!important}.input ._select-container ng-select._select ::ng-deep .ng-select-container{border:1px solid #E0E0E0;border-radius:10px;background-color:#f1f1f1;box-shadow:none}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container{padding:5px 5px 5px 7px;gap:5px}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value{display:flex;flex-direction:row;align-items:stretch;background-color:#fff;border-radius:5px;font-size:14px;color:#000;margin-bottom:0}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value .ng-value-icon.left{border-right-color:#999;display:flex;height:auto;align-items:center;justify-content:center}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value .ng-value-icon.left:hover{background-color:#d3d3d3;border-top-left-radius:5px;border-bottom-left-radius:5px}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-input:hover,.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container input:hover{cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "component", type: i5.NgSelectComponent, selector: "ng-select", inputs: ["bindLabel", "bindValue", "markFirst", "placeholder", "notFoundText", "typeToSearchText", "addTagText", "loadingText", "clearAllText", "appearance", "dropdownPosition", "appendTo", "loading", "closeOnSelect", "hideSelected", "selectOnTab", "openOnEnter", "maxSelectedItems", "groupBy", "groupValue", "bufferAmount", "virtualScroll", "selectableGroup", "selectableGroupAsModel", "searchFn", "trackByFn", "clearOnBackspace", "labelForId", "inputAttrs", "tabIndex", "readonly", "searchWhileComposing", "minTermLength", "editableSearchTerm", "keyDownFn", "typeahead", "multiple", "addTag", "searchable", "clearable", "isOpen", "items", "compareWith", "clearSearchOnAdd", "deselectOnClick"], outputs: ["blur", "focus", "change", "open", "close", "search", "clear", "add", "remove", "scroll", "scrollToEnd"] }, { kind: "component", type: i5.NgOptionComponent, selector: "ng-option", inputs: ["value", "disabled"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-select', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormSelectComponent),
                            multi: true,
                        },
                    ], template: "\n\n<div class=\"input\">\n\n    <div class=\"_label-container\">\n        <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n        <ng-content select=\"[add-icon]\"></ng-content>\n\t</div>\n\n    <div class=\"_select-container\" (click)=\"setFocus($event)\">\n        <ng-container *ngIf=\"!template\">\n            <ng-select\n                class=\"_select\"\n                [placeholder]=\"placeholder\"\n                addTagText=\"+\"\n                [bindValue]=\"bindValue\"\n                [bindLabel]=\"bindLabel\"\n                [name]=\"controlName\" \n                [formControl]=\"formControl\"\n                [items]=\"items\"\n                [multiple]=\"multiple\"\n                [closeOnSelect]=\"!multiple\"\n                [dropdownPosition]=\"position\"\n                [readonly]=\"readonly\"\n                [addTag]=\"addTag\"\n                (change)=\"onSelect($event)\"\n                [searchable]=\"searchable\"\n            >\n            </ng-select>    \n        </ng-container>\n    \n        <ng-container *ngIf=\"template\">\n            <ng-select\n                class=\"_select\"\n                [placeholder]=\"placeholder\"\n                addTagText=\"+\"\n                [name]=\"controlName\" \n                [formControl]=\"formControl\"\n                [multiple]=\"multiple\"\n                [closeOnSelect]=\"!multiple\"\n                [dropdownPosition]=\"position\"\n                [readonly]=\"readonly\"\n                [addTag]=\"addTag\"\n                [bindLabel]=\"bindLabel\"\n                (change)=\"onSelect($event)\"\n                [searchable]=\"searchable\"\n                >\n                <ng-option *ngFor=\"let item of items\" [value]=\"bindValue === 'item' ? $any(item) : bindValue ? $any(item)[bindValue] : $any(item).id\" [bindLabel]=\"$any(item)[bindLabel]\">\n                    <ng-container *ngTemplateOutlet=\"template; context: {$implicit: item}\"></ng-container>\n                </ng-option>\n            </ng-select>    \n        </ng-container>\n\n        <ng-content select=\"[add-button]\"></ng-content>\n    </div>\n\n    \n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: [".input ._label-container{margin-bottom:3px;display:flex;flex-direction:row;align-items:center;justify-content:space-between}.input ._label-container ion-icon{margin-right:10px}.input ._label-container ion-icon:hover{cursor:pointer}.input ._select-container{display:flex;align-items:center;flex-direction:row}.input ._select-container ng-select._select{flex-grow:10}.input ._select-container ng-select.ng-select-single ::ng-deep .ng-value-container .ng-value .ng-value-icon{display:none!important}.input ._select-container ng-select.ng-select-single ::ng-deep .ng-value-container .ng-value .ng-value-label{padding:0 5px!important}.input ._select-container ng-select._select ::ng-deep .ng-select-container{border:1px solid #E0E0E0;border-radius:10px;background-color:#f1f1f1;box-shadow:none}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container{padding:5px 5px 5px 7px;gap:5px}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value{display:flex;flex-direction:row;align-items:stretch;background-color:#fff;border-radius:5px;font-size:14px;color:#000;margin-bottom:0}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value .ng-value-icon.left{border-right-color:#999;display:flex;height:auto;align-items:center;justify-content:center}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-value .ng-value-icon.left:hover{background-color:#d3d3d3;border-top-left-radius:5px;border-bottom-left-radius:5px}.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container .ng-input:hover,.input ._select-container ng-select._select ::ng-deep .ng-select-container .ng-value-container input:hover{cursor:pointer}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], items: [{
                type: Input
            }], bindValue: [{
                type: Input
            }], bindLabel: [{
                type: Input
            }], formControl: [{
                type: Input
            }], multiple: [{
                type: Input
            }], position: [{
                type: Input
            }], readonly: [{
                type: Input
            }], addTag: [{
                type: Input
            }], template: [{
                type: Input
            }], searchable: [{
                type: Input
            }], selected: [{
                type: Output
            }] } });

class FormSelectModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormSelectModule, declarations: [FormSelectComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            FormLabelModule,
            FormValidationErrorModule,
            NgSelectModule], exports: [FormSelectComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormSelectModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            FormLabelModule,
            FormValidationErrorModule,
            NgSelectModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormSelectComponent],
                    exports: [FormSelectComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IonicModule,
                        FormLabelModule,
                        FormValidationErrorModule,
                        NgSelectModule,
                    ],
                    providers: [],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormTextareaComponent {
    label;
    readonly = false;
    formControl;
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormTextareaComponent, selector: "form-textarea", inputs: { label: "label", readonly: "readonly", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormTextareaComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <textarea autosize [name]=\"controlName\" [formControl]=\"formControl\" [readonly]=\"readonly\"></textarea>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input textarea{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%;background-attachment:local;resize:none;background-image:linear-gradient(to right,#F1F1F1 10px,transparent 10px),linear-gradient(to left,#F1F1F1 10px,transparent 10px),repeating-linear-gradient(#F1F1F1,#F1F1F1 29px,#E0E0E0 29px,#E0E0E0 30px,#F1F1F1 30px);line-height:30px}._input textarea:focus{outline:none}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "directive", type: i4.AutosizeDirective, selector: "[autosize]", inputs: ["minRows", "autosize", "maxRows", "onlyGrow", "useImportant"], outputs: ["resized"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-textarea', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormTextareaComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <textarea autosize [name]=\"controlName\" [formControl]=\"formControl\" [readonly]=\"readonly\"></textarea>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input textarea{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%;background-attachment:local;resize:none;background-image:linear-gradient(to right,#F1F1F1 10px,transparent 10px),linear-gradient(to left,#F1F1F1 10px,transparent 10px),repeating-linear-gradient(#F1F1F1,#F1F1F1 29px,#E0E0E0 29px,#E0E0E0 30px,#F1F1F1 30px);line-height:30px}._input textarea:focus{outline:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormTextareaModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaModule, declarations: [FormTextareaComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormValidationErrorModule,
            FormLabelModule,
            AutosizeModule], exports: [FormTextareaComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormValidationErrorModule,
            FormLabelModule,
            AutosizeModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormTextareaComponent],
                    exports: [FormTextareaComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        FormValidationErrorModule,
                        FormLabelModule,
                        AutosizeModule,
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

const INPUT_DATE_STRING_VALIDATION = /((0[1-9]|[12]\d|3[01])(-|\/)(0[1-9]|1[0-2])(-|\/)([12]\d{3}))/;
class FormDateComponent {
    label;
    readonly = false;
    formControl;
    placeholder = 'Selecteer of type een datum (dd/mm/jjjj)';
    controlName;
    value;
    dateValue;
    formattedDate = '';
    id = `date-picker-input-${Math.random() * 100}`;
    disabled = false;
    onChange = (_) => { };
    onTouched = () => { };
    constructor() { }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    onInputValueChange(event) {
        const date = this.parseDate(event?.target?.value);
        this.updateValue(date);
    }
    ngOnChanges() {
        const date = this.parseDate(this.formControl.value);
        this.updateValue(date);
    }
    onDateTimeSelected({ value }) {
        this.updateValue(value);
    }
    onPickerOpen() {
        const dialog = document.getElementsByClassName('owl-dialog-container')[0];
        dialog.addEventListener('mousedown', this.setFocusOnInput.bind(this));
    }
    setFocusOnInput() {
        const element = document.getElementById(this.id);
        const scrollParent = this.getScrollParent(element);
        if (scrollParent) {
            const scrollTop = scrollParent.scrollTop;
            scrollParent?.scrollTo(0, scrollTop);
            setTimeout(() => {
                scrollParent?.scrollTo(0, scrollTop);
            });
        }
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    getScrollParent(node) {
        if (node === null) {
            return null;
        }
        if (node.scrollHeight > node.clientHeight) {
            return node;
        }
        else {
            return this.getScrollParent(node.parentNode);
        }
    }
    parseDate(value) {
        if (INPUT_DATE_STRING_VALIDATION.test(value)) {
            const valueAsArray = value.split(INPUT_DATE_STRING_VALIDATION);
            return new Date(valueAsArray[6], valueAsArray[4] - 1, valueAsArray[2], 0, 0, 0, 0);
        }
        else if (value instanceof Date) {
            return value;
        }
        else {
            return null;
        }
    }
    updateValue(date) {
        this.formControl.setValue(date, { emitEvent: false });
        if (date) {
            this.formattedDate = format(date, 'dd/MM/yyyy');
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormDateComponent, selector: "form-date", inputs: { label: "label", readonly: "readonly", formControl: "formControl", placeholder: "placeholder" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormDateComponent),
                multi: true,
            },
        ], usesOnChanges: true, ngImport: i0, template: "<div class=\"input\">\n  <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\n  <div class=\"_date-time-picker-inner-container\" [id]=\"id\">\n    <input\n      class=\"_date-input _manual-input\"\n      [value]=\"formattedDate\"\n      [placeholder]=\"placeholder\"\n      [readonly]=\"readonly\"\n      (change)=\"onInputValueChange($event)\"\n      (keyup)=\"onInputValueChange($event)\"\n    />\n    <input hidden [formControl]=\"formControl\">\n    <input\n      (dateTimeChange)=\"onDateTimeSelected($event)\"\n      class=\"_date-input _picker-input\"\n      [owlDateTime]=\"date\"\n      [placeholder]=\"placeholder\"\n      [readonly]=\"readonly\"\n      [(ngModel)]=\"dateValue\"\n    />\n    <ion-icon *ngIf=\"!readonly && !formControl.disabled\" src=\"./assets/icons/calendar-outline.svg\" [owlDateTimeTrigger]=\"date\"></ion-icon>\n    <owl-date-time\n      #date\n      pickerType=\"calendar\"\n      [pickerMode]=\"'dialog'\"\n      [firstDayOfWeek]=\"1\"\n      (afterPickerOpen)=\"onPickerOpen()\"\n    ></owl-date-time>\n  </div>\n\n  <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>\n", styles: [".input ._date-time-picker-inner-container{position:relative}.input ._date-time-picker-inner-container ion-icon{position:absolute;right:10px;font-size:19px;top:7px}.input ._date-time-picker-inner-container ion-icon:hover{cursor:pointer}.input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 40px 6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}.input input:focus{outline:none}.input input._picker-input{display:none}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: i3.IonIcon, selector: "ion-icon", inputs: ["color", "flipRtl", "icon", "ios", "lazy", "md", "mode", "name", "sanitize", "size", "src"] }, { kind: "directive", type: i4$1.OwlDateTimeTriggerDirective, selector: "[owlDateTimeTrigger]", inputs: ["owlDateTimeTrigger", "disabled"] }, { kind: "directive", type: i4$1.OwlDateTimeInputDirective, selector: "input[owlDateTime]", inputs: ["owlDateTime", "owlDateTimeFilter", "_disabled", "min", "max", "selectMode", "rangeSeparator", "value", "values"], outputs: ["dateTimeChange", "dateTimeInput"], exportAs: ["owlDateTimeInput"] }, { kind: "component", type: i4$1.OwlDateTimeComponent, selector: "owl-date-time", inputs: ["backdropClass", "panelClass", "startAt", "pickerType", "pickerMode", "disabled", "opened", "scrollStrategy"], outputs: ["afterPickerClosed", "afterPickerOpen", "yearSelected", "monthSelected"], exportAs: ["owlDateTime"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-date', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormDateComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"input\">\n  <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\n  <div class=\"_date-time-picker-inner-container\" [id]=\"id\">\n    <input\n      class=\"_date-input _manual-input\"\n      [value]=\"formattedDate\"\n      [placeholder]=\"placeholder\"\n      [readonly]=\"readonly\"\n      (change)=\"onInputValueChange($event)\"\n      (keyup)=\"onInputValueChange($event)\"\n    />\n    <input hidden [formControl]=\"formControl\">\n    <input\n      (dateTimeChange)=\"onDateTimeSelected($event)\"\n      class=\"_date-input _picker-input\"\n      [owlDateTime]=\"date\"\n      [placeholder]=\"placeholder\"\n      [readonly]=\"readonly\"\n      [(ngModel)]=\"dateValue\"\n    />\n    <ion-icon *ngIf=\"!readonly && !formControl.disabled\" src=\"./assets/icons/calendar-outline.svg\" [owlDateTimeTrigger]=\"date\"></ion-icon>\n    <owl-date-time\n      #date\n      pickerType=\"calendar\"\n      [pickerMode]=\"'dialog'\"\n      [firstDayOfWeek]=\"1\"\n      (afterPickerOpen)=\"onPickerOpen()\"\n    ></owl-date-time>\n  </div>\n\n  <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>\n", styles: [".input ._date-time-picker-inner-container{position:relative}.input ._date-time-picker-inner-container ion-icon{position:absolute;right:10px;font-size:19px;top:7px}.input ._date-time-picker-inner-container ion-icon:hover{cursor:pointer}.input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 40px 6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}.input input:focus{outline:none}.input input._picker-input{display:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }], placeholder: [{
                type: Input
            }] } });

class DefaultIntl extends OwlDateTimeIntl {
    /** A label for the up second button (used by screen readers).  */
    upSecondLabel = 'Seconde toevoegen';
    /** A label for the down second button (used by screen readers).  */
    downSecondLabel = 'Seconde aftrekken';
    /** A label for the up minute button (used by screen readers).  */
    upMinuteLabel = 'Minuut toevoegen';
    /** A label for the down minute button (used by screen readers).  */
    downMinuteLabel = 'Minuut aftrekken';
    /** A label for the up hour button (used by screen readers).  */
    upHourLabel = 'Uur toevoegen';
    /** A label for the down hour button (used by screen readers).  */
    downHourLabel = 'Uur aftrekken';
    /** A label for the previous month button (used by screen readers). */
    prevMonthLabel = 'Vorige maand';
    /** A label for the next month button (used by screen readers). */
    nextMonthLabel = 'Volgende maand';
    /** A label for the previous year button (used by screen readers). */
    prevYearLabel = 'Vorig jaar';
    /** A label for the next year button (used by screen readers). */
    nextYearLabel = 'Volgend jaar';
    /** A label for the previous multi-year button (used by screen readers). */
    prevMultiYearLabel = 'Vorige 21 jaar';
    /** A label for the next multi-year button (used by screen readers). */
    nextMultiYearLabel = 'Volgende 21 jaar';
    /** A label for the 'switch to month view' button (used by screen readers). */
    switchToMonthViewLabel = 'Verander naar maand weergave';
    /** A label for the 'switch to year view' button (used by screen readers). */
    switchToMultiYearViewLabel = 'Kies maand en jaar';
    /** A label for the cancel button */
    cancelBtnLabel = 'Annuleren';
    /** A label for the set button */
    setBtnLabel = 'Kies';
    /** A label for the range 'from' in picker info */
    rangeFromLabel = 'Van';
    /** A label for the range 'to' in picker info */
    rangeToLabel = 'Tot';
    /** A label for the hour12 button (AM) */
    hour12AMLabel = 'AM';
    /** A label for the hour12 button (PM) */
    hour12PMLabel = 'PM';
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultIntl, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultIntl });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultIntl, decorators: [{
            type: Injectable
        }] });

class FormDateModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormDateModule, declarations: [FormDateComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            OwlDateTimeModule,
            OwlNativeDateTimeModule,
            FormValidationErrorModule,
            FormLabelModule], exports: [FormDateComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateModule, providers: [{ provide: OwlDateTimeIntl, useClass: DefaultIntl }], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            OwlDateTimeModule,
            OwlNativeDateTimeModule,
            FormValidationErrorModule,
            FormLabelModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormDateComponent],
                    exports: [FormDateComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IonicModule,
                        OwlDateTimeModule,
                        OwlNativeDateTimeModule,
                        FormValidationErrorModule,
                        FormLabelModule,
                    ],
                    providers: [{ provide: OwlDateTimeIntl, useClass: DefaultIntl }],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormDateRangeComponent {
    label;
    readonly = false;
    startsAtFormControl;
    endsAtFormControl;
    controlName;
    value;
    default;
    id = `date-range-picker-input-${Math.random() * 100}`;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        if (this.startsAtFormControl.valid && this.endsAtFormControl.valid) {
            setTimeout(() => {
                this.default = [this.startsAtFormControl.value, this.endsAtFormControl.value];
            }, 1);
        }
    }
    onDateTimeSelected({ value }) {
        this.default = null;
        this.startsAtFormControl?.setValue(value[0]);
        this.endsAtFormControl?.setValue(value[1]);
    }
    onPickerOpen() {
        const dialog = document.getElementsByClassName('owl-dialog-container')[0];
        dialog.addEventListener('mousedown', this.setFocusOnInput.bind(this));
    }
    setFocusOnInput() {
        const element = document.getElementById(this.id);
        const scrollParent = this.getScrollParent(element);
        if (scrollParent) {
            const scrollTop = scrollParent.scrollTop;
            scrollParent?.scrollTo(0, scrollTop);
            setTimeout(() => {
                scrollParent?.scrollTo(0, scrollTop);
            });
        }
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    getScrollParent(node) {
        if (node === null) {
            return null;
        }
        if (node.scrollHeight > node.clientHeight) {
            return node;
        }
        else {
            return this.getScrollParent(node.parentNode);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormDateRangeComponent, selector: "form-date-range", inputs: { label: "label", readonly: "readonly", startsAtFormControl: "startsAtFormControl", endsAtFormControl: "endsAtFormControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormDateRangeComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"input\">\n    <form-label [label]=\"label\" [formControl]=\"startsAtFormControl\"></form-label>\n    \n    <div class=\"_date-time-picker-inner-container\" [id]=\"id\">\n        <span class=\"_date-range\" [owlDateTimeTrigger]=\"dt\">\n            <input #dateInput (dateTimeChange)=\"onDateTimeSelected($event)\" class=\"_date-input\" selectMode=\"range\" rangeSeparator=\"-\" [owlDateTime]=\"dt\" autoSizeInput [(ngModel)]=\"default\">\n            <owl-date-time #dt pickerType=\"calendar\" [firstDayOfWeek]=\"1\"></owl-date-time>\n            <ion-icon src=\"./assets/icons/arrow-small-down.svg\"></ion-icon>\n        </span>\n    </div>\n\n    <form-validation-error [control]=\"startsAtFormControl\"></form-validation-error>\n</div>", styles: [".input ._date-time-picker-inner-container{position:relative}.input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 40px 6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}.input input:focus{outline:none}.input input._picker-input{display:none}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: i3.IonIcon, selector: "ion-icon", inputs: ["color", "flipRtl", "icon", "ios", "lazy", "md", "mode", "name", "sanitize", "size", "src"] }, { kind: "directive", type: i4$1.OwlDateTimeTriggerDirective, selector: "[owlDateTimeTrigger]", inputs: ["owlDateTimeTrigger", "disabled"] }, { kind: "directive", type: i4$1.OwlDateTimeInputDirective, selector: "input[owlDateTime]", inputs: ["owlDateTime", "owlDateTimeFilter", "_disabled", "min", "max", "selectMode", "rangeSeparator", "value", "values"], outputs: ["dateTimeChange", "dateTimeInput"], exportAs: ["owlDateTimeInput"] }, { kind: "component", type: i4$1.OwlDateTimeComponent, selector: "owl-date-time", inputs: ["backdropClass", "panelClass", "startAt", "pickerType", "pickerMode", "disabled", "opened", "scrollStrategy"], outputs: ["afterPickerClosed", "afterPickerOpen", "yearSelected", "monthSelected"], exportAs: ["owlDateTime"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-date-range', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormDateRangeComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"input\">\n    <form-label [label]=\"label\" [formControl]=\"startsAtFormControl\"></form-label>\n    \n    <div class=\"_date-time-picker-inner-container\" [id]=\"id\">\n        <span class=\"_date-range\" [owlDateTimeTrigger]=\"dt\">\n            <input #dateInput (dateTimeChange)=\"onDateTimeSelected($event)\" class=\"_date-input\" selectMode=\"range\" rangeSeparator=\"-\" [owlDateTime]=\"dt\" autoSizeInput [(ngModel)]=\"default\">\n            <owl-date-time #dt pickerType=\"calendar\" [firstDayOfWeek]=\"1\"></owl-date-time>\n            <ion-icon src=\"./assets/icons/arrow-small-down.svg\"></ion-icon>\n        </span>\n    </div>\n\n    <form-validation-error [control]=\"startsAtFormControl\"></form-validation-error>\n</div>", styles: [".input ._date-time-picker-inner-container{position:relative}.input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 40px 6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}.input input:focus{outline:none}.input input._picker-input{display:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], startsAtFormControl: [{
                type: Input
            }], endsAtFormControl: [{
                type: Input
            }] } });

class FormDateRangeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeModule, declarations: [FormDateRangeComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            OwlDateTimeModule,
            OwlNativeDateTimeModule,
            FormValidationErrorModule,
            FormLabelModule], exports: [FormDateRangeComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeModule, providers: [{ provide: OwlDateTimeIntl, useClass: DefaultIntl }], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IonicModule,
            OwlDateTimeModule,
            OwlNativeDateTimeModule,
            FormValidationErrorModule,
            FormLabelModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormDateRangeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormDateRangeComponent],
                    exports: [FormDateRangeComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IonicModule,
                        OwlDateTimeModule,
                        OwlNativeDateTimeModule,
                        FormValidationErrorModule,
                        FormLabelModule,
                    ],
                    providers: [{ provide: OwlDateTimeIntl, useClass: DefaultIntl }],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormTimeComponent {
    label;
    readonly = false;
    autocomplete = 'off';
    formControl;
    min;
    max;
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTimeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormTimeComponent, selector: "form-time", inputs: { label: "label", readonly: "readonly", autocomplete: "autocomplete", formControl: "formControl", min: "min", max: "max" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormTimeComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input type=\"time\" [name]=\"controlName\" [formControl]=\"formControl\" [autocomplete]=\"autocomplete === 'off' ? 'no-auto' : autocomplete\" [readonly]=\"readonly\" [min]=\"min\" [max]=\"max\">\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTimeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-time', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormTimeComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input type=\"time\" [name]=\"controlName\" [formControl]=\"formControl\" [autocomplete]=\"autocomplete === 'off' ? 'no-auto' : autocomplete\" [readonly]=\"readonly\" [min]=\"min\" [max]=\"max\">\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], formControl: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }] } });

class FormTimeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTimeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormTimeModule, declarations: [FormTimeComponent], imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule], exports: [FormTimeComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTimeModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormTimeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormTimeComponent],
                    exports: [FormTimeComponent],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormNumberComponent {
    label;
    min;
    max;
    readonly = false;
    formControl;
    controlName;
    value;
    id = `number-input-${Math.random() * 100}`;
    disabled;
    onChange;
    onTouched;
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    add() {
        const current = parseFloat(this.value);
        if (this.max && this.max <= current) {
            return;
        }
        const next = current + 1;
        this.updateValue(next);
    }
    subtract() {
        const current = parseFloat(this.value);
        if (current > 0) {
            if (this.min && this.min >= current) {
                return;
            }
            const next = current - 1;
            this.updateValue(next);
        }
    }
    updateValue(value) {
        this.inputChanged(value);
        document.getElementById(this.id).value = value.toString();
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormNumberComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormNumberComponent, selector: "form-number", inputs: { label: "label", min: "min", max: "max", readonly: "readonly", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormNumberComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <div class=\"container\">\n\t\t<div class=\"action minus\" (click)=\"subtract()\">-</div>\n\t\t<input [id]=\"id\" autocomplete=\"no-auto\" type=\"number\" inputmode=\"numeric\" [name]=\"controlName\" [formControl]=\"formControl\" [readonly]=\"readonly\">\n\t\t<div class=\"action plus\" (click)=\"add()\">+</div>\n\t</div>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input .container{display:flex;flex-direction:row;align-items:stretch}._input .container .action{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 20px;border-radius:10px;font-size:20px}._input .container .action:hover{cursor:pointer}._input .container .action.plus{border-radius:0 10px 10px 0;border-left:none}._input .container .action.minus{border-radius:10px 0 0 10px;border-right:none}._input .container input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;font-size:14px;color:#000;width:100%;text-align:center}._input .container input:focus{outline:none}._input .container input::-webkit-outer-spin-button,._input .container input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}._input .container input[type=number]{-moz-appearance:textfield}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-number', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormNumberComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <div class=\"container\">\n\t\t<div class=\"action minus\" (click)=\"subtract()\">-</div>\n\t\t<input [id]=\"id\" autocomplete=\"no-auto\" type=\"number\" inputmode=\"numeric\" [name]=\"controlName\" [formControl]=\"formControl\" [readonly]=\"readonly\">\n\t\t<div class=\"action plus\" (click)=\"add()\">+</div>\n\t</div>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input .container{display:flex;flex-direction:row;align-items:stretch}._input .container .action{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 20px;border-radius:10px;font-size:20px}._input .container .action:hover{cursor:pointer}._input .container .action.plus{border-radius:0 10px 10px 0;border-left:none}._input .container .action.minus{border-radius:10px 0 0 10px;border-right:none}._input .container input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;font-size:14px;color:#000;width:100%;text-align:center}._input .container input:focus{outline:none}._input .container input::-webkit-outer-spin-button,._input .container input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}._input .container input[type=number]{-moz-appearance:textfield}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormNumberModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormNumberModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormNumberModule, declarations: [FormNumberComponent], imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule], exports: [FormNumberComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormNumberModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormNumberModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormNumberComponent],
                    exports: [FormNumberComponent],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule],
                    providers: [],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormColorComponent {
    label;
    readonly = false;
    formControl;
    controlName;
    color;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(color) {
        this.color = color;
        this.onChange(this.color);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    onChangeColor(color) {
        this.color = color;
        this.formControl.setValue(color);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(color) {
        this.color = color;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormColorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormColorComponent, selector: "form-color", inputs: { label: "label", readonly: "readonly", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormColorComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input \n        [name]=\"controlName\" \n        [readonly]=\"true\" \n        [cpDisabled]=\"readonly\" \n        [(colorPicker)]=\"color\" \n        [style.background]=\"color\" \n        (colorPickerChange)=\"onChangeColor($event)\" \n        cpOutputFormat=\"hex\" \n        cpAlphaChannel=\"disabled\"\n        cpPosition=\"top\"\n        cpPositionOffset=\"50\"\n    >\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}._input input:hover{cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "directive", type: i4$2.ColorPickerDirective, selector: "[colorPicker]", inputs: ["colorPicker", "cpWidth", "cpHeight", "cpToggle", "cpDisabled", "cpIgnoredElements", "cpFallbackColor", "cpColorMode", "cpCmykEnabled", "cpOutputFormat", "cpAlphaChannel", "cpDisableInput", "cpDialogDisplay", "cpSaveClickOutside", "cpCloseClickOutside", "cpUseRootViewContainer", "cpPosition", "cpPositionOffset", "cpPositionRelativeToArrow", "cpOKButton", "cpOKButtonText", "cpOKButtonClass", "cpCancelButton", "cpCancelButtonText", "cpCancelButtonClass", "cpEyeDropper", "cpPresetLabel", "cpPresetColors", "cpPresetColorsClass", "cpMaxPresetColorsLength", "cpPresetEmptyMessage", "cpPresetEmptyMessageClass", "cpAddColorButton", "cpAddColorButtonText", "cpAddColorButtonClass", "cpRemoveColorButtonClass", "cpArrowPosition", "cpExtraTemplate"], outputs: ["cpInputChange", "cpToggleChange", "cpSliderChange", "cpSliderDragEnd", "cpSliderDragStart", "colorPickerOpen", "colorPickerClose", "colorPickerCancel", "colorPickerSelect", "colorPickerChange", "cpCmykColorChange", "cpPresetColorsChange"], exportAs: ["ngxColorPicker"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormColorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-color', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormColorComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n    <input \n        [name]=\"controlName\" \n        [readonly]=\"true\" \n        [cpDisabled]=\"readonly\" \n        [(colorPicker)]=\"color\" \n        [style.background]=\"color\" \n        (colorPickerChange)=\"onChangeColor($event)\" \n        cpOutputFormat=\"hex\" \n        cpAlphaChannel=\"disabled\"\n        cpPosition=\"top\"\n        cpPositionOffset=\"50\"\n    >\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._input input{background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:100%}._input input:focus{outline:none}._input input:hover{cursor:pointer}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormColorModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormColorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormColorModule, declarations: [FormColorComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormLabelModule,
            FormValidationErrorModule,
            ColorPickerModule], exports: [FormColorComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormColorModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormLabelModule,
            FormValidationErrorModule,
            ColorPickerModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormColorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormColorComponent],
                    exports: [FormColorComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        FormLabelModule,
                        FormValidationErrorModule,
                        ColorPickerModule,
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormRadioComponent {
    label;
    placeholder;
    items;
    bindValue;
    bindLabel;
    formControl;
    readonly = false;
    selected = new EventEmitter();
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnChanges(changes) {
        if (changes['items'].currentValue) {
            this.items?.map((item) => {
                if (this.bindLabel) {
                    item.label = item[this.bindLabel];
                }
            });
        }
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onSelect(change) {
        this.selected.emit(change);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormRadioComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormRadioComponent, selector: "form-radio", inputs: { label: "label", placeholder: "placeholder", items: "items", bindValue: "bindValue", bindLabel: "bindLabel", formControl: "formControl", readonly: "readonly" }, outputs: { selected: "selected" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormRadioComponent),
                multi: true,
            },
        ], usesOnChanges: true, ngImport: i0, template: "\n<div class=\"input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\n    <ion-radio-group [formControl]=\"formControl\">\n        <div class=\"_radio-container\">\n            <ion-item *ngFor=\"let item of items\">\n                <ion-radio color=\"success\" label-placement=\"end\" justify=\"start\" value=\"{{bindValue ? $any(item)[bindValue] : item }}\">{{bindLabel ? $any(item)[bindLabel] : item }}</ion-radio>\n            </ion-item>\n        </div>\n    </ion-radio-group>\n    \n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: [".input ion-radio-group ._radio-container{border-radius:15px;padding:15px;border:1px solid #E0E0E0}.input ion-radio-group ion-item{--border-color: transparent;--padding-start: 5px;--min-height: 40px;border-radius:10px}.input ion-radio-group ion-radio{margin-right:10px!important;color:#4b4237;font-size:14px;margin:0}.input ion-radio-group p{color:#4b4237;font-size:14px;margin:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: i3.IonItem, selector: "ion-item", inputs: ["button", "color", "counter", "counterFormatter", "detail", "detailIcon", "disabled", "download", "fill", "href", "lines", "mode", "rel", "routerAnimation", "routerDirection", "shape", "target", "type"] }, { kind: "component", type: i3.IonRadio, selector: "ion-radio", inputs: ["alignment", "color", "disabled", "justify", "labelPlacement", "legacy", "mode", "name", "value"] }, { kind: "component", type: i3.IonRadioGroup, selector: "ion-radio-group", inputs: ["allowEmptySelection", "compareWith", "name", "value"] }, { kind: "directive", type: i3.RadioValueAccessor, selector: "ion-radio" }, { kind: "directive", type: i3.SelectValueAccessor, selector: "ion-select, ion-radio-group, ion-segment, ion-datetime" }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-radio', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormRadioComponent),
                            multi: true,
                        },
                    ], template: "\n<div class=\"input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\n    <ion-radio-group [formControl]=\"formControl\">\n        <div class=\"_radio-container\">\n            <ion-item *ngFor=\"let item of items\">\n                <ion-radio color=\"success\" label-placement=\"end\" justify=\"start\" value=\"{{bindValue ? $any(item)[bindValue] : item }}\">{{bindLabel ? $any(item)[bindLabel] : item }}</ion-radio>\n            </ion-item>\n        </div>\n    </ion-radio-group>\n    \n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: [".input ion-radio-group ._radio-container{border-radius:15px;padding:15px;border:1px solid #E0E0E0}.input ion-radio-group ion-item{--border-color: transparent;--padding-start: 5px;--min-height: 40px;border-radius:10px}.input ion-radio-group ion-radio{margin-right:10px!important;color:#4b4237;font-size:14px;margin:0}.input ion-radio-group p{color:#4b4237;font-size:14px;margin:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], items: [{
                type: Input
            }], bindValue: [{
                type: Input
            }], bindLabel: [{
                type: Input
            }], formControl: [{
                type: Input
            }], readonly: [{
                type: Input
            }], selected: [{
                type: Output
            }] } });

class FormRadioModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormRadioModule, declarations: [FormRadioComponent], imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, FormLabelModule, FormValidationErrorModule], exports: [FormRadioComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormRadioModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, FormLabelModule, FormValidationErrorModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormRadioComponent],
                    exports: [FormRadioComponent],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, FormLabelModule, FormValidationErrorModule],
                    providers: [],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormEditorComponent {
    label;
    readonly = false;
    formControl;
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormEditorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormEditorComponent, selector: "form-editor", inputs: { label: "label", readonly: "readonly", formControl: "formControl" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormEditorComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n   \n    <quill-editor \n            [formControl]=\"formControl\" \n            [format]=\"'html'\"\n            [formats]=\"[\n                'background',\n                'bold',\n                'color',\n                'font',\n                'italic',\n                'size',\n                'strike',\n                'script',\n                'underline',\n                'blockquote',\n                'header',\n                'indent',\n                'list',\n                'align',\n                'direction',\n                'variable',\n                'width',\n                'height',\n                'style',\n                'link'\n            ]\"\n            [sanitize]=\"true\"\n            [customOptions]=\"[{\n                import: 'attributors/style/size',\n                whitelist: ['10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px']\n            }]\"\n            [modules]=\"{\n                toolbar: [\n                    ['bold', 'italic', 'underline', 'strike'],       \n                    ['blockquote'],        \n                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],\n                    [{ 'script': 'sub'}, { 'script': 'super' }],     \n                    [{ 'indent': '-1'}, { 'indent': '+1' }],\n                    [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px'] }],\n                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],\n                    [{ 'color': [] }, { 'background': [] }],        \n                    [{ 'align': [] }],\n                    ['link']  \n                ]\n            }\"\n        ></quill-editor>\n\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css?family=Open+Sans\";._input quill-editor{width:100%}._input quill-editor .ql-toolbar{border-top-left-radius:10px;border-top-right-radius:10px;background-color:#f1f1f1;border:1px solid #E0E0E0}.ql-snow .ql-picker.ql-size .ql-picker-label[data-value]:not(._input quill-editor .ql-toolbar:first-child):before,.ql-snow .ql-picker.ql-size .ql-picker-item[data-value]:not(._input quill-editor .ql-toolbar:first-child):before{content:attr(data-value)!important}._input quill-editor .ql-container{border-bottom-left-radius:10px;border-bottom-right-radius:10px;background-color:#f1f1f1;border:1px solid #E0E0E0;font-family:Open Sans,serif!important}\n"], dependencies: [{ kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: i4$3.QuillEditorComponent, selector: "quill-editor" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-editor', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormEditorComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_input\">\n    <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n   \n    <quill-editor \n            [formControl]=\"formControl\" \n            [format]=\"'html'\"\n            [formats]=\"[\n                'background',\n                'bold',\n                'color',\n                'font',\n                'italic',\n                'size',\n                'strike',\n                'script',\n                'underline',\n                'blockquote',\n                'header',\n                'indent',\n                'list',\n                'align',\n                'direction',\n                'variable',\n                'width',\n                'height',\n                'style',\n                'link'\n            ]\"\n            [sanitize]=\"true\"\n            [customOptions]=\"[{\n                import: 'attributors/style/size',\n                whitelist: ['10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px']\n            }]\"\n            [modules]=\"{\n                toolbar: [\n                    ['bold', 'italic', 'underline', 'strike'],       \n                    ['blockquote'],        \n                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],\n                    [{ 'script': 'sub'}, { 'script': 'super' }],     \n                    [{ 'indent': '-1'}, { 'indent': '+1' }],\n                    [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px'] }],\n                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],\n                    [{ 'color': [] }, { 'background': [] }],        \n                    [{ 'align': [] }],\n                    ['link']  \n                ]\n            }\"\n        ></quill-editor>\n\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css?family=Open+Sans\";._input quill-editor{width:100%}._input quill-editor .ql-toolbar{border-top-left-radius:10px;border-top-right-radius:10px;background-color:#f1f1f1;border:1px solid #E0E0E0}.ql-snow .ql-picker.ql-size .ql-picker-label[data-value]:not(._input quill-editor .ql-toolbar:first-child):before,.ql-snow .ql-picker.ql-size .ql-picker-item[data-value]:not(._input quill-editor .ql-toolbar:first-child):before{content:attr(data-value)!important}._input quill-editor .ql-container{border-bottom-left-radius:10px;border-bottom-right-radius:10px;background-color:#f1f1f1;border:1px solid #E0E0E0;font-family:Open Sans,serif!important}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }] } });

class FormEditorModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormEditorModule, declarations: [FormEditorComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormValidationErrorModule,
            FormLabelModule, i4$3.QuillModule], exports: [FormEditorComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormEditorModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FormValidationErrorModule,
            FormLabelModule,
            QuillModule.forRoot()] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormEditorComponent],
                    exports: [FormEditorComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        FormValidationErrorModule,
                        FormLabelModule,
                        QuillModule.forRoot(),
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class FormCheckboxGroupComponent {
    label;
    checkboxes;
    readonly = false;
    formControl;
    bindLabel;
    bindValue;
    controlName;
    value;
    disabled;
    onChange;
    onTouched;
    id = `checkboxgroup-${Math.random() * 100}`; // generate unique id to couple the label and the checkbox
    checkedBoxes = [];
    constructor() {
        this.onChange = (_) => { };
        this.onTouched = () => { };
        this.disabled = false;
    }
    inputChanged(value) {
        this.value = value;
        this.onChange(this.value);
        this.onTouched();
    }
    ngOnInit() {
        this.controlName = this.getControlName(this.formControl);
    }
    ngOnChanges(changes) {
        if (changes['formControl']?.currentValue?.value) {
            this.checkedBoxes = changes['formControl']?.currentValue.value;
        }
    }
    isChecked(name) {
        return this.checkedBoxes.includes(name);
    }
    getControlName(c) {
        const formGroup = c?.parent?.controls;
        if (formGroup) {
            return Object.keys(formGroup).find((name) => c === formGroup[name]) || null;
        }
        return null;
    }
    onToggle(e) {
        if (e.target.checked) {
            this.checkedBoxes.push(e.target.name);
        }
        else {
            this.checkedBoxes = pull(this.checkedBoxes, e.target.name);
        }
        this.onChange(this.checkedBoxes);
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: FormCheckboxGroupComponent, selector: "form-checkboxgroup", inputs: { label: "label", checkboxes: "checkboxes", readonly: "readonly", formControl: "formControl", bindLabel: "bindLabel", bindValue: "bindValue" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FormCheckboxGroupComponent),
                multi: true,
            },
        ], usesOnChanges: true, ngImport: i0, template: "<div class=\"_checkboxgroup\">\n    <div class=\"_label-container\">\n        <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\t</div>\n    <div class=\"_checkboxes\">\n        <div class=\"_checkbox\" *ngFor=\"let _checkbox of checkboxes; index as i\">\n            <input type=\"checkbox\" id=\"_{{i + id}}\" name=\"{{_checkbox[bindValue]}}\" (change)=\"onToggle($event)\" [checked]=\"isChecked(_checkbox[bindValue])\">\n            <label for=\"_{{i + id}}\" [innerHtml]=\"_checkbox[bindLabel]\"></label>\n        </div> \n    </div>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._checkboxgroup ._checkboxes{border-radius:15px;padding:15px;border:1px solid #E0E0E0}._checkboxgroup ._checkboxes ._checkbox{display:flex;gap:18px;padding-left:5px;align-items:start;cursor:pointer}._checkboxgroup ._checkboxes ._checkbox:not(:last-of-type){margin-bottom:18px}._checkboxgroup ._checkboxes ._checkbox input{flex-shrink:0;background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:18px;height:18px}._checkboxgroup ._checkboxes ._checkbox input:focus{outline:none}._checkboxgroup ._checkboxes ._checkbox label{padding:0;font-size:14px;color:#4b4237}._checkboxgroup ._checkboxes form-validation-error{margin-top:5px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: FormLabelComponent, selector: "form-label", inputs: ["label", "formControl"] }, { kind: "component", type: FormValidationErrorComponent, selector: "form-validation-error", inputs: ["control"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'form-checkboxgroup', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FormCheckboxGroupComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"_checkboxgroup\">\n    <div class=\"_label-container\">\n        <form-label [label]=\"label\" [formControl]=\"formControl\"></form-label>\n\t</div>\n    <div class=\"_checkboxes\">\n        <div class=\"_checkbox\" *ngFor=\"let _checkbox of checkboxes; index as i\">\n            <input type=\"checkbox\" id=\"_{{i + id}}\" name=\"{{_checkbox[bindValue]}}\" (change)=\"onToggle($event)\" [checked]=\"isChecked(_checkbox[bindValue])\">\n            <label for=\"_{{i + id}}\" [innerHtml]=\"_checkbox[bindLabel]\"></label>\n        </div> \n    </div>\n    <form-validation-error [control]=\"formControl\"></form-validation-error>\n</div>", styles: ["._checkboxgroup ._checkboxes{border-radius:15px;padding:15px;border:1px solid #E0E0E0}._checkboxgroup ._checkboxes ._checkbox{display:flex;gap:18px;padding-left:5px;align-items:start;cursor:pointer}._checkboxgroup ._checkboxes ._checkbox:not(:last-of-type){margin-bottom:18px}._checkboxgroup ._checkboxes ._checkbox input{flex-shrink:0;background-color:#f1f1f1;border:1px solid #E0E0E0;padding:6px 10px;border-radius:10px;font-size:14px;color:#000;width:18px;height:18px}._checkboxgroup ._checkboxes ._checkbox input:focus{outline:none}._checkboxgroup ._checkboxes ._checkbox label{padding:0;font-size:14px;color:#4b4237}._checkboxgroup ._checkboxes form-validation-error{margin-top:5px}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], checkboxes: [{
                type: Input
            }], readonly: [{
                type: Input
            }], formControl: [{
                type: Input
            }], bindLabel: [{
                type: Input
            }], bindValue: [{
                type: Input
            }] } });

class FormCheckboxGroupModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupModule, declarations: [FormCheckboxGroupComponent], imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule], exports: [FormCheckboxGroupComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormCheckboxGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FormCheckboxGroupComponent],
                    exports: [FormCheckboxGroupComponent],
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, FormLabelModule, FormValidationErrorModule],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/*
 * Public API Surface of ngx-form-components
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FormCheckboxGroupComponent, FormCheckboxGroupModule, FormColorComponent, FormColorModule, FormDateComponent, FormDateModule, FormDateRangeComponent, FormDateRangeModule, FormEditorComponent, FormEditorModule, FormInputComponent, FormInputModule, FormLabelComponent, FormLabelModule, FormNumberComponent, FormNumberModule, FormRadioComponent, FormRadioModule, FormSelectComponent, FormSelectModule, FormTextareaComponent, FormTextareaModule, FormTimeComponent, FormTimeModule, FormValidationErrorComponent, FormValidationErrorModule };
